import { memo, useCallback } from 'react';
import { type Control, useController } from 'react-hook-form';

import { Field, FieldLabel, FieldText } from '@air/primitive-field';

import { type DemoFormInput } from '~/components/Modals/demoFormValidation';
import { type OnSelectionChangeArgs } from '~/components/Select/Select';
import { SingleSelect } from '~/components/Select/SingleSelect';

const COMPANY_SIZE_OPTIONS = [
  { label: '1-10', value: '1-10' },
  { label: '11-50', value: '11-50' },
  { label: '51-100', value: '51-100' },
  { label: '101-500', value: '101-500' },
  { label: '501-1000', value: '501-1000' },
  { label: '1001+', value: '1001+' },
];

export type DemoFormCompanySizeFieldProps = {
  control: Control<DemoFormInput>;
  formId: string;
};

export const DemoFormCompanySizeField = memo(({ control, formId }: DemoFormCompanySizeFieldProps) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ control, name: 'company_size' });

  const selectedOption = COMPANY_SIZE_OPTIONS.find((option) => option.value === value);
  const fieldId = `${formId}-company_size`;
  const errorId = `${fieldId}-error`;

  const handleSelectionChange = useCallback(
    ({ chips }: OnSelectionChangeArgs) => {
      onChange(chips[0]?.value ?? '');
    },
    [onChange],
  );

  return (
    <Field className="w-full">
      <FieldLabel className="sr-only" htmlFor={fieldId}>
        Company size
      </FieldLabel>
      <SingleSelect
        hasError={!!error}
        inputAriaDescribedBy={error ? errorId : undefined}
        inputAriaInvalid={!!error}
        inputId={fieldId}
        onSelectionChange={handleSelectionChange}
        options={COMPANY_SIZE_OPTIONS}
        placeholder="Company size*"
        selectedOption={selectedOption}
        size="large"
      />
      {error?.message ? (
        <FieldText color="red" id={errorId}>
          {error.message}
        </FieldText>
      ) : null}
    </Field>
  );
});

DemoFormCompanySizeField.displayName = 'DemoFormCompanySizeField';
